import React, { useState, useMemo, useCallback } from 'react';
import { Form, Col, Row, Input, Select, message, Radio } from 'antd';
import { FormItem, Button } from 'components/elements';
import {
  useAddFamilyRelationAtOnceMutation,
  FamilyTreeAttributes,
  FamilyTree,
  SecondaryRelationTypeEnum,
} from 'graphql/types';

type ParentType = Omit<FromFields, 'mother' | 'father' | 'secondaryFather' | 'secondaryMother'>;
interface FromFields {
  type: 'parent' | 'partner' | 'child' | 'secondaryParent';
  fullName: string;
  partnerId: string;
  secondaryRelationType: SecondaryRelationTypeEnum;
  mother: ParentType;
  father: ParentType;
  secondaryFather: ParentType;
  secondaryMother: ParentType;
}
interface TreePanelProps {
  data: FamilyTree[];
  refetch: () => void;
}

export interface OpenDrawerProps {
  id: string;
}

const TreePanel = ({ data, refetch }: TreePanelProps) => {
  const [drawerSetting, setDrawerSetting] = useState({ visible: false, id: '' });
  const [form] = Form.useForm();
  const [relationType, setRelationType] = useState('');
  const [addFamilyRelation, { loading }] = useAddFamilyRelationAtOnceMutation();

  const pageReset = useCallback(() => {
    setRelationType('');
    setDrawerSetting({ visible: false, id: '' });
    form.resetFields();
  }, [form]);

  const dataById = useMemo(() => {
    return data.reduce<Record<string, FamilyTree>>(
      (acc, item) => ({
        ...acc,
        [item.id]: { ...item },
      }),
      {},
    );
  }, [data]);

  const selectedItem = useMemo(() => {
    return dataById[drawerSetting.id];
  }, [dataById, drawerSetting.id]);

  const findPartners = useCallback(() => {
    const partners = data.filter((node) => {
      return node.isPartner && node.pid === selectedItem.id;
    });
    if (selectedItem.isPartner) {
      partners.push(data.find((node) => node.id === selectedItem.pid));
    }
    return partners;
  }, [data, selectedItem]);

  const parentOptions = useMemo(() => {
    if (relationType === 'child') {
      const partners = findPartners();
      const optionResult = partners.map((partner) => (
        <Radio value={partner.id}>
          {selectedItem.fullName} and {partner.fullName}
        </Radio>
      ));
      if (!selectedItem.isPartner) {
        optionResult.push(<Radio value="myself">Only {selectedItem.fullName}</Radio>);
      }
      return optionResult;
    }
    return null;
  }, [findPartners, relationType, selectedItem]);

  const openDrawer = useCallback(({ id }: OpenDrawerProps) => {
    setDrawerSetting({ visible: true, id });
  }, []);

  const onClose = useCallback(() => {
    pageReset();
  }, [pageReset]);

  const onSubmit = useCallback(() => {
    form.submit();
  }, [form]);

  const onFinish = async (values: FromFields) => {
    const { type, partnerId, mother, father, secondaryFather, secondaryMother, secondaryRelationType, ...restValues } =
      values;
    const attributes: FamilyTreeAttributes = {
      ...restValues,
    };
    if (type === 'partner') {
      attributes.partners = [selectedItem.id];
    }
    if (type === 'child') {
      attributes.parents = [selectedItem.id];
      if (partnerId !== 'myself') {
        attributes.parents.push(partnerId);
      }
    }

    try {
      if (type === 'parent') {
        const fatherRecord = await addFamilyRelation({
          variables: { input: { ...father, children: [selectedItem.id] } },
        });
        const fatherId = fatherRecord?.data?.addFamilyRelationAtOnce?.data?.id;

        if (Object.values(mother).filter((a) => a).length) {
          await addFamilyRelation({
            variables: { input: { ...mother, children: [selectedItem.id], partners: [fatherId] } },
          });
        }
      } else if (type === 'secondaryParent') {
        const fatherRecord = await addFamilyRelation({
          variables: { input: { ...secondaryFather, secondaryChildren: [selectedItem.id], secondaryRelationType } },
        });
        const fatherId = fatherRecord?.data?.addFamilyRelationAtOnce?.data?.id;

        if (Object.values(secondaryMother).filter((a) => a).length) {
          await addFamilyRelation({
            variables: { input: { ...secondaryMother, secondaryChildren: [selectedItem.id], partners: [fatherId] } },
          });
        }
      } else {
        await addFamilyRelation({
          variables: { input: attributes },
        });
      }
      pageReset();
      refetch();
    } catch (err) {
      if (err instanceof Error) {
        message.error(err.message);
      }
    }
  };

  const drawerPanel = {
    title: `Add Relation to ${selectedItem?.fullName}`,
    onClose,
    visible: drawerSetting.visible,
    footer: (
      <div
        style={{
          textAlign: 'right',
        }}
      >
        <Button onClick={onClose} style={{ marginRight: 8 }} type="primary" ghost>
          Cancel
        </Button>
        <Button onClick={onSubmit} type="primary" loading={loading}>
          Submit
        </Button>
      </div>
    ),
    children: (
      <div>
        <Form
          layout="vertical"
          //   validateTrigger="onChange"
          preserve={false}
          form={form}
          onFinish={onFinish}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <FormItem
                theme="dark"
                name="type"
                validateStatus="error"
                label="Relation Type"
                rules={[
                  { required: true, message: 'Please Select Relation Type!' },
                  ({ getFieldValue }) => ({
                    validateTrigger: 'onChange',
                    validator: (_, value) => {
                      if (getFieldValue('type') === 'partner' && selectedItem.isPartner) {
                        return Promise.reject(
                          new Error(
                            'You Cannot Add Partner to this Person in this Tree! Please try to add in their tree.',
                          ),
                        );
                      }
                      if (getFieldValue('type') === 'parent') {
                        if (selectedItem.isPartner) {
                          return Promise.reject(
                            new Error(
                              'Parent cannot be added for spouse in this tree! Please try to add in their own tree.',
                            ),
                          );
                        }
                        if (selectedItem.pid) {
                          return Promise.reject(
                            new Error('This Person Already have Parent! please edit current parent.'),
                          );
                        }
                      }
                      if (getFieldValue('type') === 'secondaryParent') {
                        if (!selectedItem.pid) {
                          return Promise.reject(
                            new Error('You must add primary parent before you able to add secondary parent!'),
                          );
                        }
                        if (selectedItem.isPartner) {
                          return Promise.reject(
                            new Error(
                              'Secondary Parent cannot be added for spouse in this tree! Please try to add in their own tree.',
                            ),
                          );
                        }
                      }
                      setRelationType(value);
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Select
                  placeholder="Please select Relation Type"
                  options={[
                    { label: 'Parent', value: 'parent' },
                    { label: 'Secondary Parent', value: 'secondaryParent' },
                    { label: 'Partner', value: 'partner' },
                    { label: 'Child', value: 'child' },
                  ]}
                />
              </FormItem>
            </Col>
            <Col xs={24} sm={12}>
              {relationType === 'partner' && (
                <FormItem theme="dark" name="partnerPosition" label="Which Side To Display?">
                  <Radio.Group>
                    <Radio value="left">Left</Radio>
                    <Radio value="right">Right</Radio>
                  </Radio.Group>
                </FormItem>
              )}
            </Col>
          </Row>
          {relationType === 'child' && (
            <Row>
              <Col span={24}>
                <FormItem
                  theme="dark"
                  name="partnerId"
                  label="Who are the parents?"
                  rules={[{ required: true, message: 'Please select one from the list!' }]}
                >
                  <Radio.Group>{parentOptions}</Radio.Group>
                </FormItem>
              </Col>
            </Row>
          )}
          {!!relationType && !['parent', 'secondaryParent'].includes(relationType) && (
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <FormItem
                  theme="dark"
                  name="fullName"
                  label="Full Name"
                  rules={[{ required: true, message: 'Please input your Full Name!' }]}
                >
                  <Input />
                </FormItem>
              </Col>
            </Row>
          )}
          {relationType === 'parent' &&
            [
              { name: 'father', required: true, label: 'Father' },
              { name: 'mother', required: false, label: 'Mother' },
            ].map((parent) => (
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <FormItem
                    theme="dark"
                    name={[parent.name, 'fullName']}
                    label={`${parent.label} Full Name`}
                    rules={[parent.required && { required: true, message: `${parent.label} Full Name is required!` }]}
                  >
                    <Input />
                  </FormItem>
                </Col>
              </Row>
            ))}
          {relationType === 'secondaryParent' && (
            <Row gutter={16}>
              {[
                { name: 'secondaryFather', required: true, label: 'Father' },
                { name: 'secondaryMother', required: false, label: 'Mother' },
              ].map((parent) => (
                <Col xs={24} sm={12}>
                  <FormItem
                    theme="dark"
                    name={[parent.name, 'fullName']}
                    label={`${parent.label} Full Name`}
                    rules={[parent.required && { required: true, message: `${parent.label} Full Name is required!` }]}
                  >
                    <Input />
                  </FormItem>
                </Col>
              ))}
              <Col xs={24} sm={12}>
                <FormItem
                  theme="dark"
                  name="secondaryRelationType"
                  label="Secondary Parent Relation Type"
                  rules={[{ required: true, message: `Parent Relation Type is required!` }]}
                >
                  <Select
                    placeholder="Please select Relation Type"
                    options={[
                      { label: 'Biological', value: 'biological' },
                      { label: 'Adoptive', value: 'adoptive' },
                    ]}
                  />
                </FormItem>
              </Col>
            </Row>
          )}
        </Form>
      </div>
    ),
  };

  return [drawerPanel, openDrawer] as const;
};

export default TreePanel;
